<template>
  <div class="c-main">
    <h2 class="c-title c-title--page">
      {{ $t('local.import.title') }}
    </h2>
    <div class="c-main--flex-1">
      <div class="o-layout">
        <!-- LOGIN PHASE -->
        <div class="o-layout__item">
          <h3 class="c-title c-title--page-section">1. {{ $t('local.import.login') }}</h3>
        </div>
        <div v-if="phase === 1" class="o-layout__item u-1-of-2-at-small">
          <form id="import-login-form">
            <div v-if="loginFeedback" class="c-form--error">{{loginFeedback}}</div>
            <sceTextField v-model="username" name="username" label="usernameOrEmail"
                          v-validate data-vv-rules="required"
                          :class="{'is-invalid': errors.has('username')}" />
            <scePasswordField v-model="password" name="password" label="password"
                              v-validate data-vv-rules="required"
                              :class="{'is-invalid': errors.has('password')}" />
            <sceButton label="login" @click="login" class="c-button--primary" />
          </form>
        </div>
        <div v-if="phase > 1" class="o-layout__item">
          {{ userConfig.user.name }}
        </div>

        <!-- ORGANISATION PHASE -->
        <div class="o-layout__item u-margin-top-medium">
          <h3 class="c-title c-title--page-section">2. {{ $t('local.import.organisation') }}</h3>
        </div>
        <div v-if="phase === 2" class="o-layout__item">
          <sceSelect label="organisation" :options="organisations" v-model="organisationId" />
          <sceButton label="select" @click="selectOrganisation" class="c-button--primary" />
        </div>
        <div v-if="phase > 2" class="o-layout__item">
          {{ organisation.name }}
        </div>

        <!-- EVENT PHASE -->
        <div class="o-layout__item u-margin-top-medium">
          <h3 class="c-title c-title--page-section">3. {{ $t('local.import.event') }}</h3>
        </div>
        <div v-if="phase === 3" class="o-layout__item">
          <grid :config="{searchFields: ['title', 'venue', 'date']}" :data="events" class="u-1-of-1">
            <template v-slot:gridHeader>
              <div class="o-layout c-list__row u-padding-horizontal-small">
                <div class="o-layout__item u-3-of-6-at-medium">
                  {{$t('title')}}
                </div>
                <div class="o-layout__item u-1-of-6-at-medium u-1-of-2-at-tiny">
                  {{$t('venue')}}
                </div>
                <div class="o-layout__item u-2-of-6-at-medium u-1-of-2-at-tiny">
                  {{$t('date')}}
                </div>
              </div>
            </template>
            <template v-slot:gridData="{element}">
              <div class="o-layout__item u-3-of-7-at-medium c-list__main-cell">
                {{element.title}}
              </div>
              <div class="o-layout__item u-1-of-7-at-medium u-1-of-2-at-tiny">
                {{element.venue}}
              </div>
              <div class="o-layout__item u-2-of-7-at-medium u-1-of-2-at-tiny">
                {{element.date}}
              </div>
              <div class="o-layout__item u-1-of-7-at-medium u-1-of-2-at-tiny">
                <sceButton label="select" @click="selectEvent(element)" class="c-button--primary" />
              </div>
            </template>
          </grid>
        </div>
        <div v-if="phase > 3" class="o-layout__item">
          {{ event.title }} - {{ event.date }} - {{ event.venue }}
        </div>

        <!-- SYNC PHASE -->
        <div class="o-layout__item u-margin-top-medium">
          <h3 class="c-title c-title--page-section">4. {{ $t('local.import.sync') }}</h3>
        </div>
        <div v-if="phase === 4" class="o-layout__item">
          <sceButton label="start" @click="startImport" class="c-button--primary" />
        </div>
        <div v-if="phase === 5" class="o-layout__item">
          <template v-if="importStatus === 'started'">
            <h4 class="c-title c-title--page-section">
              {{ $t('import.syncing') }}
            </h4>
            <div class="sk-circle">
              <div class="sk-circle1 sk-child"></div>
              <div class="sk-circle2 sk-child"></div>
              <div class="sk-circle3 sk-child"></div>
              <div class="sk-circle4 sk-child"></div>
              <div class="sk-circle5 sk-child"></div>
              <div class="sk-circle6 sk-child"></div>
              <div class="sk-circle7 sk-child"></div>
              <div class="sk-circle8 sk-child"></div>
              <div class="sk-circle9 sk-child"></div>
              <div class="sk-circle10 sk-child"></div>
              <div class="sk-circle11 sk-child"></div>
              <div class="sk-circle12 sk-child"></div>
            </div>
          </template>
          <template v-if="importStatus === 'finished'">
            <h4 class="c-title c-title--page-section">
              {{ $t('import.finished')  }}
            </h4>
            <sceRouteButton label="events" :route="{ name: 'local.events' }" class="c-button--primary"/>
          </template>
          <div v-if="importStatus === 'error'" class="c-icon c-icon--error"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import sortBy from 'lodash/sortBy'
import moment from "moment/moment"

import config from '@/config'

export default {
  name: 'LocalImport',
  components: {
    grid: require('client/components/dataGrid.vue').default,
  },
  data: function () {
    return {
      urlBase: null,
      phase: 1,
      username: null,
      password: null,
      loginFeedback: null,
      token: null,
      userConfig: null,
      organisations: [],
      organisationId: null,
      organisation: null,
      events: [],
      event: null,
      importStatus: '',
    }
  },
  created: function () {
    this.urlBase = this.$store.state.environment.remoteApp
  },
  methods: {
    getHeaders: function() {
      const headers = {
        'Access-Control-Allow-Origin': '*'
      }
      if (this.token) {
        headers['Authorization'] = this.token.id
      }
      return headers
    },
    login: function () {
      this.$validator.validateAll()
      if (!this.errors.any()) {
        const creds = { username: this.username, password: this.password, type: 'regular' }
        const options = {
          headers: this.getHeaders(),
        }
        this.$http.post(this.urlBase + '/seUsers/sce-login', creds, options).then(result => {
          this.token = result.body
          const options = {headers: this.getHeaders()}
          this.$http.get(this.urlBase +'/seUsers/' + this.token.userId + '/config', options).then((result) => {
            this.userConfig = result.data
            this.password = null
            if (result.data.contextRoles.general === 'admin') {
              this.$http.get(this.urlBase +'/organisations', options).then(result => {
                this.setOrganisations(result.data)
              })
            }
            else {
              this.setOrganisations(result.data.organisations.map(o => ({
                id: o.organisationId,
                name: o.name,
              })))
            }
          })
        }, err => {
          console.log(err)
          this.loginFeedback = this.$t('authentication.error')
        })
      }
    },
    setOrganisations: function(organisations) {
      this.organisations = sortBy(organisations.filter(o => o.name), ['name'])
      if (this.organisations.length === 1) {
        this.organisation = this.organisations[0]
        this.loadEvents()
      }
      else {
        this.phase = 2
      }
    },
    selectOrganisation: function() {
      this.organisation = this.organisations.find(o => o.id === this.organisationId)
      this.loadEvents()
    },
    loadEvents: function() {
      this.$http.get(this.urlBase + '/organisations/' + this.organisation.id + '/eventList',
        {headers: this.getHeaders()}).then(result => {
          this.events = sortBy(result.data, ['date_from', 'title']).reverse()
          this.events.forEach(e => {
            e.date = this.format(e.date_from) + ' - ' + this.format(e.date_to)
          })
        this.phase = 3
      })
    },
    format: function(date) {
      return moment(date).format('DD/MM/YYYY')
    },
    selectEvent: function(event) {
      this.event = event
      this.phase = 4
    },
    startImport: function() {
      this.importStatus = 'started'
      this.$http.post(config.root + '/syncDatabase/' + this.organisation.id + '/' + this.event.id).then(() =>{
        this.importStatus = 'finished'
      }, (error) => {
        console.log(error)
        this.importStatus = 'error'
      })
      this.phase = 5
    }
  },
}
</script>
